import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import BootstrapTheme from '@fullcalendar/bootstrap';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import HolidayFormModal from './HolidayFormModal';
import {
  fetchWarehouses,
  fetchHolidays,
  saveHoliday,
  deleteHoliday,
  seedDefaultHolidays,
  fetchReadyByDate,
} from './actions/holidayActions';

import '@fullcalendar/bootstrap/main.css';
import './holidayCalendar.css';

const HolidayCalendar = () => {
  const dispatch = useDispatch();
  const { warehouses, holidays, isLoading } = useSelector(
    (state) => state.holiday,
  );
  const selectedCountry = useSelector((state) => state.country.country);
  const userObj = JSON.parse(localStorage.getItem('authUser') || '{}');

  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [existing, setExisting] = useState(null);
  const [rbdModalOpen, setRbdModalOpen] = useState(false);
  const [rbdLoading, setRbdLoading] = useState(false);
  const [rbdResult, setRbdResult] = useState(null);
  const iconClickRef = useRef(false);
  const [visibleRange, setVisibleRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  });

  // Load warehouses once.
  useEffect(() => {
    fetchWarehouses({ dispatch });
  }, [dispatch]);

  // Reload holidays whenever the warehouse or visible range changes.
  const reloadHolidays = useCallback(() => {
    if (!selectedWarehouse) return;
    fetchHolidays({
      params: {
        warehouse_code: selectedWarehouse.value,
        from_date: visibleRange.start,
        to_date: visibleRange.end,
      },
      dispatch,
    });
  }, [selectedWarehouse, visibleRange, dispatch]);

  useEffect(() => {
    reloadHolidays();
  }, [reloadHolidays]);

  const warehouseOptions = useMemo(() => {
    const allOptions = warehouses.map((w) => ({
      value: w.warehouse_code,
      label: `${w.warehouse_name} (${w.country})`,
      country: w.country,
    }));

    // If a specific country is selected from the top sidebar, filter by it
    if (selectedCountry && selectedCountry.toLowerCase() !== 'all') {
      return allOptions.filter(
        (w) => w.country === selectedCountry.toUpperCase(),
      );
    }

    // "All" selected: admin sees all, others see only their allocated countries
    if (String(userObj?.role_id) === '1') return allOptions;
    const userCountries = Object.keys(userObj?.countryList || {}).map((c) =>
      c.toUpperCase(),
    );
    return allOptions.filter((w) => userCountries.includes(w.country));
  }, [warehouses, selectedCountry, userObj?.role_id, userObj?.countryList]);

  // Reset selected warehouse when country changes and it's no longer in the list
  useEffect(() => {
    if (selectedWarehouse) {
      const stillExists = warehouseOptions.some(
        (w) => w.value === selectedWarehouse.value,
      );
      if (!stillExists) setSelectedWarehouse(null);
    }
  }, [warehouseOptions]);

  // Map holidays to FullCalendar events.
  const events = useMemo(
    () =>
      holidays.map((h) => ({
        id: String(h.id),
        title: h.holiday_name,
        start: h.holiday_date,
        allDay: true,
        backgroundColor:
          h.type === 'weekly_off'
            ? '#edaf3e'
            : h.type === 'working_day'
              ? '#34c38f'
              : h.type === 'default'
                ? '#556ee6'
                : '#f46a6a',
        borderColor:
          h.type === 'weekly_off'
            ? '#c1871d'
            : h.type === 'working_day'
              ? '#2a9d75'
              : h.type === 'default'
                ? '#556ee6'
                : '#f46a6a',
        extendedProps: { raw: h },
      })),
    [holidays],
  );

  const openAddModal = (dateStr) => {
    if (!selectedWarehouse) {
      return;
    }
    // Block past dates — users can only add holidays for today or later.
    const today = new Date().toISOString().slice(0, 10);
    const match = holidays.find((h) => h.holiday_date === dateStr);
    if (dateStr < today && !match) {
      toast.error('Cannot add holidays on past dates');
      return;
    }
    setSelectedDate(dateStr);
    setExisting(match || null);
    setModalOpen(true);
  };

  const handleDateClick = (arg) => {
    if (iconClickRef.current) {
      iconClickRef.current = false;
      return;
    }
    openAddModal(arg.dateStr);
  };
  const handleEventClick = (arg) => {
    const raw = arg.event.extendedProps.raw;
    if (raw) openAddModal(raw.holiday_date);
  };

  const handleSave = (values) => {
    saveHoliday({
      params: { warehouse_code: selectedWarehouse.value, ...values },
      dispatch,
      onSuccess: () => {
        setModalOpen(false);
        reloadHolidays();
      },
    });
  };

  const handleDelete = (date) => {
    deleteHoliday({
      params: { warehouse_code: selectedWarehouse.value, holiday_date: date },
      dispatch,
      onSuccess: () => {
        setModalOpen(false);
        reloadHolidays();
      },
    });
  };

  const handleSeedDefaults = () => {
    if (!selectedWarehouse) return;
    seedDefaultHolidays({
      params: { warehouse_code: selectedWarehouse.value },
      dispatch,
      onSuccess: reloadHolidays,
    });
  };

  const handleReadyByClick = async (dateStr) => {
    if (!selectedWarehouse) {
      toast.error('Select a warehouse first');
      return;
    }
    setRbdModalOpen(true);
    setRbdLoading(true);
    setRbdResult(null);
    const data = await fetchReadyByDate({
      params: { warehouse_code: selectedWarehouse.value, date: dateStr },
      dispatch,
    });
    setRbdLoading(false);
    setRbdResult(data);
  };

  // Render a small clock icon in the top-right of every day cell.
  // Only show on today and future dates.
  const renderDayCellContent = (arg) => {
    const y = arg.date.getFullYear();
    const m = String(arg.date.getMonth() + 1).padStart(2, '0');
    const d = String(arg.date.getDate()).padStart(2, '0');
    const cellDate = `${y}-${m}-${d}`;
    const today = new Date().toISOString().slice(0, 10);
    const showIcon = selectedWarehouse && cellDate >= today;
    return (
      <>
        <span className="rbd-day-num">{arg.dayNumberText}</span>
        {showIcon && (
          <i
            className="bx bx-time-five rbd-icon"
            title="Show ready-by-date"
            onMouseDown={() => {
              iconClickRef.current = true;
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleReadyByClick(cellDate);
            }}
          />
        )}
      </>
    );
  };

  const handleDatesSet = (arg) => {
    setVisibleRange({
      start: arg.startStr.slice(0, 10),
      end: arg.endStr.slice(0, 10),
    });
  };

  return (
    <div className="page-content">
      <MetaTags>
        <title>Delivery Holiday Calendar</title>
      </MetaTags>
      <Container fluid>
        <Breadcrumbs
          title="Settings"
          breadcrumbItem="Delivery Holiday Calendar"
        />

        <Row className="mb-3">
          <Col md={4}>
            <Select
              placeholder="Select warehouse"
              options={warehouseOptions}
              value={selectedWarehouse}
              onChange={setSelectedWarehouse}
              isClearable
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : null
              }
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </Col>
          <Col md={8} className="text-end">
            <Button
              color="secondary"
              disabled={!selectedWarehouse}
              onClick={handleSeedDefaults}
            >
              Seed Country Defaults
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card className="holiday-calendar-card">
              <CardBody>
                {!selectedWarehouse && (
                  <div className="text-muted mb-3">
                    Select a warehouse to view and manage holidays.
                  </div>
                )}
                <div style={{ position: 'relative' }}>
                  {isLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255,255,255,0.6)',
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Spinner color="primary" />
                    </div>
                  )}
                  <FullCalendar
                    plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin]}
                    themeSystem="bootstrap"
                    initialView="dayGridMonth"
                    fixedWeekCount={false}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,dayGridWeek',
                    }}
                    events={events}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    datesSet={handleDatesSet}
                    dayCellContent={renderDayCellContent}
                    height="auto"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={rbdModalOpen}
        toggle={() => setRbdModalOpen(!rbdModalOpen)}
        centered
      >
        <ModalHeader toggle={() => setRbdModalOpen(false)}>
          Ready-By-Date
        </ModalHeader>
        <ModalBody>
          {rbdLoading && <div className="text-muted">Calculating...</div>}
          {!rbdLoading && rbdResult && (
            <>
              <p className="mb-2">
                <strong>Warehouse:</strong> {rbdResult.warehouse_code}
              </p>
              <p className="mb-2">
                <strong>Order Date:</strong> {rbdResult.order_date}
              </p>
              <p className="mb-3">
                <strong>Ready By:</strong>{' '}
                <span className="text-success">{rbdResult.ready_by_date}</span>
              </p>
              {rbdResult.applied_holidays &&
                Object.keys(rbdResult.applied_holidays).length > 0 && (
                  <>
                    <h6 className="mt-3">Holidays that pushed the date</h6>
                    <Table size="sm" bordered className="mb-3">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(rbdResult.applied_holidays).map(
                          ([d, n]) => (
                            <tr key={d}>
                              <td>{d}</td>
                              <td>{n}</td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              {rbdResult.holidays_within_30_days && (
                <>
                  <h6 className="mt-3">Holidays within 30 days</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Weekday</th>
                        <th>Name</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rbdResult.holidays_within_30_days.map((h) => (
                        <tr key={`${h.date}-${h.type}`}>
                          <td>{h.date}</td>
                          <td>{h.weekday}</td>
                          <td>{h.name}</td>
                          <td>{h.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      <HolidayFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        selectedDate={selectedDate}
        existing={existing}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default HolidayCalendar;
